import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useCashfreeVbankPaymentsQuery, useCashfreeVbanksQuery } from 'v2/pages/billing/queries'
import { getAccountId } from 'v2/pages/billing/utils'
import { billingQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast, successToast } from 'v2/utils/toasts'
import { BankTransferDrawerView } from './bank-transfer-drawer-view'

interface CashfreeBankTransferDrawerProps {
  open: boolean
  onClose: () => void
}

const VBANK_POLL_INTERVAL_MS = 15_000

// Cashfree data container for the shared BankTransferDrawerView. The
// recharge-modal flow proactively fetches/creates a vbank before opening
// (CashfreeRechargeModal.handlePay) so the happy path skips the error state;
// settings-page callers fall through to the error state if no vbank has ever
// been provisioned.
export const CashfreeBankTransferDrawer = ({ open, onClose }: CashfreeBankTransferDrawerProps) => {
  const { data: vbanks, isLoading } = useCashfreeVbanksQuery(open)
  const activeVbank = vbanks?.find((vb) => vb.status === 'ACTIVE')
  const accountId = getAccountId()
  const queryClient = useQueryClient()

  const vbankId = activeVbank?.virtual_account_id || ''
  const { data: vbankPayments } = useCashfreeVbankPaymentsQuery(vbankId, 20, 0, {
    enabled: open && !!vbankId,
    refetchInterval: open && vbankId ? VBANK_POLL_INTERVAL_MS : undefined,
  })

  // Baseline = payment_ids seen on the first poll while the drawer is open.
  // New SUCCESS arrivals after baseline trigger toast + balance refresh;
  // FAILED arrivals toast and join the baseline so we don't re-fire. PENDING
  // entries are deliberately skipped — they stay outside baseline so the
  // status transition (PENDING -> SUCCESS) on the same payment_id is caught
  // on a later poll.
  const baselineRef = useRef<Set<number> | null>(null)
  const [hasHandledNewPayment, setHasHandledNewPayment] = useState(false)

  useEffect(() => {
    if (!open) {
      baselineRef.current = null
      setHasHandledNewPayment(false)
    }
  }, [open])

  useEffect(() => {
    if (!open || !vbankPayments) return
    if (baselineRef.current === null) {
      baselineRef.current = new Set(vbankPayments.map((p) => p.cf_payment_id))
      return
    }
    if (hasHandledNewPayment) return

    const newSuccess = vbankPayments.find(
      (p) => !baselineRef.current?.has(p.cf_payment_id) && p.payment_status === 'SUCCESS',
    )
    if (newSuccess) {
      if (accountId) {
        queryClient.invalidateQueries({ queryKey: billingQueryKeys().balanceAll(accountId) })
        queryClient.invalidateQueries({ queryKey: billingQueryKeys().transactionsAll(accountId) })
      }
      successToast({
        title: 'Payment received',
        description: 'Your balance will reflect shortly.',
      })
      setHasHandledNewPayment(true)
      onClose()
      return
    }

    const newFailed = vbankPayments.find(
      (p) => !baselineRef.current?.has(p.cf_payment_id) && p.payment_status === 'FAILED',
    )
    if (newFailed) {
      baselineRef.current?.add(newFailed.cf_payment_id)
      errorToast({
        title: 'Payment failed',
        description: 'Bank transfer did not succeed. Please try again.',
      })
    }
  }, [open, vbankPayments, hasHandledNewPayment, accountId, queryClient, onClose])

  return (
    <BankTransferDrawerView
      open={open}
      onClose={onClose}
      isLoading={isLoading}
      accountNumber={
        activeVbank
          ? activeVbank.virtual_account_number || activeVbank.virtual_account_id
          : undefined
      }
      ifscCode={activeVbank?.virtual_ifsc}
      bankName={activeVbank?.virtual_bank_name}
    />
  )
}
